import {AxiosCall, CancelTokenSource} from '../axios';
import {environment} from '../environment/environment';
import {ServiceSuccessResponseType} from './service-mappers/BaseMapper';
import {
  MeApiResponseType,
  UserAuthMetaParams,
  UserLogoutParams,
  GetMeParams,
  SetNotificationParams,
  NotificationData
} from './service-mappers/CommonServiceMapper';

const meProfile = environment.apiEndPoints.profile.me;
const changePassword = environment.apiEndPoints.profile.changePassword;
const saveUserAuthMetaUrl = environment.apiEndPoints.profile.userAuthMeta;
const userLogoutUrl = environment.apiEndPoints.profile.logout;
const setNotificationUrl = environment.apiEndPoints.profile.setNotification;
const getNotificationUrl = environment.apiEndPoints.profile.getNotifications;
const userLogoutFromWebUrl = environment.apiEndPoints.profile.logoff;
const guarantorLogOff =
  environment.apiEndPoints.guarantorPayments.endPoints.guarantorLogoff;

// Added canceltoken to cancel me api, if the me api calls are made in quick succession
export const getMyProfile = async (
  params?: GetMeParams,
  CancelToken?: CancelTokenSource
): Promise<ServiceSuccessResponseType<MeApiResponseType>> => {
  const urlParams = (params && params.device_id
    ? `?device_id=${params.device_id}`
    : ''
  ).trim();
  return AxiosCall.get(`${meProfile}${urlParams}`, CancelToken);
};

export const updateProfileService = async (payload: any): Promise<any> => {
  return AxiosCall.put(meProfile, payload);
};

export const changePasswordService = async (payload: any): Promise<any> => {
  return AxiosCall.put(changePassword, payload);
};

export const saveUserAuthMeta = async (
  payload: UserAuthMetaParams
): Promise<ServiceSuccessResponseType<any>> => {
  return AxiosCall.post(saveUserAuthMetaUrl, payload);
};

export const logoutUser = async (
  payload: UserLogoutParams
): Promise<ServiceSuccessResponseType<any>> => {
  return AxiosCall.post(userLogoutUrl, payload);
};
// Service to logout or logoff user from web
export const logoutUserFromWeb = async (): Promise<
  ServiceSuccessResponseType<any>
> => {
  return AxiosCall.get(userLogoutFromWebUrl);
};
export const logoutGuarantor = async (): Promise<
  ServiceSuccessResponseType<any>
> => {
  return AxiosCall.get(guarantorLogOff);
};

export const setNotification = async (
  payload: SetNotificationParams
): Promise<ServiceSuccessResponseType<any>> => {
  return AxiosCall.put(setNotificationUrl, payload);
};

export const getNotifications = async (): Promise<
  ServiceSuccessResponseType<Array<NotificationData>>
> => {
  return AxiosCall.get(getNotificationUrl);
};

export const getImportErrorData = async (
  clinic_group_id: string | number,
  type: string,
  import_id?: string,
  clinic_id?: string | number
): Promise<any[]> => {
  let queryParam = '';
  if (clinic_id) {
    queryParam += queryParam
      ? `&clinic_id=${clinic_id}`
      : `?clinic_id=${clinic_id}`;
  }
  if (import_id) {
    queryParam += queryParam
      ? `&import_id=${import_id}`
      : `?import_id=${import_id}`;
  }
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/import/summary/${type}${queryParam}`
  );
};

export const previousImportedStatements = async (
  clinic_group_id: string | number,
  import_statement_id?: string,
  clinic_id?: string | number,
  search_text?: string,
  options?: {
    min_amount: string | number;
    max_amount: string | number;
    statement_status: string | number;
    limit: number;
    offset: number;
  }
): Promise<any[]> => {
  let queryParam = '';
  if (clinic_id) {
    queryParam = queryParam
      ? `&clinic_id=${clinic_id}`
      : `?clinic_id=${clinic_id}`;
  }
  if (options?.min_amount) {
    queryParam += queryParam
      ? `&min_amount=${options.min_amount}`
      : `?min_amount=${options.min_amount}`;
  }
  if (options?.max_amount) {
    queryParam += queryParam
      ? `&max_amount=${options.max_amount}`
      : `?max_amount=${options.max_amount}`;
  }
  if (options?.statement_status) {
    queryParam += queryParam
      ? `&statement_status=${options.statement_status}`
      : `?statement_status=${options.statement_status}`;
  }
  if (options?.limit) {
    queryParam += queryParam
      ? `&limit=${options.limit}`
      : `?limit=${options.limit}`;
  }
  if (options?.offset) {
    queryParam += queryParam
      ? `&offset=${options.offset}`
      : `?offset=${options.offset}`;
  }
  if (search_text) {
    queryParam += queryParam
      ? `&search_text=${search_text}`
      : `?search_text=${search_text}`;
  }
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/import-summary/${import_statement_id}/statements${queryParam}`
  );
};

export const getImportSummaryImportIds = async (
  clinic_group_id: string | number,
  type: string,
  clinic_id?: string | number
): Promise<any[]> => {
  let queryParam = '';
  if (clinic_id) {
    queryParam = queryParam
      ? `&clinic_id=${clinic_id}`
      : `?clinic_id=${clinic_id}`;
  }
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/import/summary/${type}/import-id${queryParam}`
  );
};

export const getMediaFile = async (params: {
  clinicGroupId: string | number;
  clinicId: string | number;
  fileId: string | number;
}): Promise<any> => {
  const {fileId, clinicGroupId, clinicId} = params;
  const url = `clinic-group/${clinicGroupId}/clinic/${clinicId}/file/${fileId}`;
  return AxiosCall.getImage(url, {responseType: 'arraybuffer'});
};

export const getGenericPatientTemplateFile = (): any => {
  return AxiosCall.getFile({url: 'file/template/generic_emr_patient_file'});
};

export const getGenericEmrStatementTemplateFile = (): any => {
  return AxiosCall.getFile({url: 'file/template/generic_emr_statement_file'});
};

export const getMessagePatientTemplateFile = (): any => {
  return AxiosCall.getFile({url: 'file/template/message_patient_file'});
};

export const getReferenceData = async ({
  reference_key
}: {
  reference_key: string;
}): Promise<any> => {
  return AxiosCall.get(`/reference-data/${reference_key}`);
};
